import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
  Avatar,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SendIcon from "@mui/icons-material/Send";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import axios from "axios";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

/**
 * Normalise a raw mobile number to the 12-digit "91XXXXXXXXXX" format
 * that eNotify expects.
 *
 * Rules:
 *  - Strip all non-digits first.
 *  - If it's already 12 digits and starts with "91" → use as-is.
 *  - If it's 10 digits → prepend "91".
 *  - Otherwise → return null (invalid).
 */
const normalisePhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  if (digits.length === 10) return `91${digits}`;
  return null; // invalid
};

/**
 * Build a clean, WhatsApp-friendly message from booking form data.
 */
const buildDefaultMessage = (form, storeName) => {
  const lines = [];

  lines.push(
    `🙏 *Thank you for choosing ${storeName || "our travel service"}!*`,
  );
  lines.push("");
  lines.push(
    "We're delighted to have you on board and look forward to making your journey truly memorable. Here are your booking details:",
  );
  lines.push("");
  lines.push("━━━━━━━━━━━━━━━━━━━━");
  lines.push("📋 *BOOKING DETAILS*");
  lines.push("━━━━━━━━━━━━━━━━━━━━");

  if (form.customerName) lines.push(`👤 *Passenger:* ${form.customerName}`);

  if (form.tripDetails) {
    lines.push("");
    lines.push("🗺️ *Trip Details:*");
    // Indent each line of trip details
    form.tripDetails
      .split("\n")
      .filter(Boolean)
      .forEach((l) => lines.push(`   ${l}`));
  }

  if (form.pickupDate) {
    const fmt = (ymd) => {
      if (!ymd) return "";
      const [y, m, d] = ymd.split("-");
      return `${d}-${m}-${y}`;
    };
    lines.push("");
    lines.push(
      `📅 *Pickup Date:* ${fmt(form.pickupDate)}${
        form.pickupTime ? `  ⏰ ${form.pickupTime}` : ""
      }`,
    );
    if (form.dropDate)
      lines.push(
        `🏁 *Drop Date:*   ${fmt(form.dropDate)}${
          form.dropTime ? `  ⏰ ${form.dropTime}` : ""
        }`,
      );
  }

  if (form.pickupAddress)
    lines.push(`📍 *Pickup Address:* ${form.pickupAddress}`);
  if (form.dropAddress) lines.push(`📍 *Drop Address:*   ${form.dropAddress}`);

  if (form.pax) lines.push(`👥 *Passengers (Pax):* ${form.pax}`);
  if (form.vehicleType) lines.push(`🚗 *Vehicle Type:* ${form.vehicleType}`);

  if (form.quote) {
    lines.push("");
    lines.push("━━━━━━━━━━━━━━━━━━━━");
    lines.push("💰 *PAYMENT SUMMARY*");
    lines.push("━━━━━━━━━━━━━━━━━━━━");
    lines.push(`   Total Quote : ₹${form.quote}`);
    if (form.advance) lines.push(`   Advance Paid: ₹${form.advance}`);
  }

  if (form.instructions) {
    lines.push("");
    lines.push(`📝 *Special Instructions:*`);
    lines.push(`   ${form.instructions}`);
  }

  lines.push("");
  lines.push("━━━━━━━━━━━━━━━━━━━━");
  lines.push("✨ *We wish you a wonderful and safe journey!*");
  lines.push(
    "Feel free to reach out for any assistance. We're here for you. 🤝",
  );

  return lines.join("\n");
};

/**
 * Convert a base64 data URI to a File object that can be appended to FormData.
 */
const dataUriToFile = (dataUri, fileName) => {
  try {
    if (dataUri.startsWith("http://") || dataUri.startsWith("https://")) {
      return null;
    }

    let base64Data;
    if (dataUri.startsWith("data:")) {
      base64Data = dataUri.split(",")[1];
    } else {
      base64Data = dataUri;
    }

    const cleanBase64 = base64Data.replace(/\s/g, "");
    const binaryString = atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: "application/pdf" });

    // Add filename property to blob (some backends check this)
    blob.lastModifiedDate = new Date();
    blob.name = fileName;

    return blob;
  } catch (err) {
    console.error("dataUriToFile error:", err);
    return null;
  }
};
export default function WhatsAppBookingDialog({
  open,
  onClose,
  form,
  setSnackbar,
}) {
  const { userInfo, templateData } = useContext(AutoTaskAuthContext) || {};
  const storeName = userInfo?.store_name || "Our Travel Agency";
  const token = templateData?.token || "";

  const normalised = normalisePhone(form?.customerMobile);
  const phoneError = form?.customerMobile && !normalised;

  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [stepLabel, setStepLabel] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Initialise message whenever dialog opens
  React.useEffect(() => {
    if (open) {
      setMessage(buildDefaultMessage(form, storeName));
      setSent(false);
      setStepLabel("");
    }
  }, [open]);

  const hasPdf = !!form?.itinerary_docs?.file;
  const pdfFileName = form?.itinerary_docs?.fileName || "Itinerary.pdf";

  const handleSend = async () => {
    if (!token) {
      setSnackbar({
        open: true,
        message: "WhatsApp token not found. Please check your settings.",
        severity: "error",
      });
      return;
    }
    if (!normalised) {
      setSnackbar({
        open: true,
        message:
          "Invalid mobile number. Must be 10 digits or 12 digits starting with 91.",
        severity: "error",
      });
      return;
    }

    setSending(true);

    try {
      let fileUrl = null;
      if (hasPdf) {
        const rawFile = form.itinerary_docs.file;

        if (rawFile.startsWith("http://") || rawFile.startsWith("https://")) {
          fileUrl = rawFile;
        } else {
          setStepLabel("Uploading PDF…");

          // Debug: Log the raw file data
          console.log("Raw file type:", typeof rawFile);
          console.log("Raw file length:", rawFile?.length);
          console.log("Raw file preview:", rawFile?.substring(0, 100));

          const file = dataUriToFile(rawFile, pdfFileName);

          if (!file) {
            throw new Error("Failed to convert itinerary PDF for upload");
          }

          // Debug: Verify file object
          console.log("File object created:");
          console.log("- Name:", file.name);
          console.log("- Size:", file.size);
          console.log("- Type:", file.type);
          console.log("- Constructor:", file.constructor.name);
          console.log("- instanceof File:", file instanceof File);
          console.log("- instanceof Blob:", file instanceof Blob);

          const fd = new FormData();
          fd.append("file", file, pdfFileName);

          // Debug: Log FormData contents
          console.log("FormData contents:");
          for (const [key, value] of fd.entries()) {
            console.log(`- Key: ${key}`);
            console.log(`  Value type:`, typeof value);
            console.log(`  Value:`, value);
            if (value instanceof Blob) {
              console.log(`  Blob size:`, value.size);
              console.log(`  Blob type:`, value.type);
            }
          }

          // Debug: Log request details
          console.log(
            "Sending request to:",
            `${API_BASE_URL}/api/auth/upload-file`,
          );

          const uploadRes = await axios.post(
            `${API_BASE_URL}/api/auth/upload-file`,
            fd,
            // No headers - let axios set them automatically
          );

          console.log("Upload response:", uploadRes.data);

          if (!uploadRes?.data?.success) {
            throw new Error(uploadRes?.data?.message || "PDF upload failed");
          }

          fileUrl = uploadRes.data.data?.file_url;
          if (!fileUrl) {
            throw new Error("No file URL returned from upload");
          }
        }
      }

      setStepLabel("Sending WhatsApp message…");

      await axios.post(`${API_BASE_URL}/api/auth/sendwpmultimediamessage`, {
        token,
        phone: normalised,
        fileUrl: fileUrl || "",
        message,
      });

      setSent(true);
      setStepLabel("");
      setSnackbar({
        open: true,
        message: `WhatsApp message sent to ${normalised} ✓`,
        severity: "success",
      });
      setTimeout(() => onClose(), 1800);
    } catch (err) {
      console.error("WhatsApp send error:", err);
      console.error("Error response:", err?.response?.data);
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err.message ||
          "Failed to send WhatsApp message",
        severity: "error",
      });
    } finally {
      setSending(false);
      setStepLabel("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!sending ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
          border: "1px solid #e8f5e9",
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          p: 0,
          background:
            "linear-gradient(135deg, #075e54 0%, #128c7e 60%, #25d366 100%)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 2.5 },
            py: 1.8,
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WhatsAppIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#fff">
                Send via WhatsApp
              </Typography>
              {normalised ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonIcon
                    sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.85)" }}
                  >
                    {form?.customerName || "Customer"}
                    {" · "}
                    <span style={{ fontWeight: 600, letterSpacing: 0.5 }}>
                      +{normalised}
                    </span>
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  No valid number
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={sending}
            sx={{ color: "#fff" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, bgcolor: "#f0f4f0" }}>
        {/* ── Phone error ── */}
        {phoneError && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Mobile number <strong>{form?.customerMobile}</strong> is invalid.
            Must be 10 digits or start with 91 (12 digits).
          </Alert>
        )}

        {!form?.customerMobile && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            No customer mobile number in the form. Please fill in the customer
            details first.
          </Alert>
        )}

        {/* ── PDF attachment badge ── */}
        {hasPdf && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              p: 1.2,
              bgcolor: "#fff",
              borderRadius: 2,
              border: "1px solid #e0e0e0",
            }}
          >
            <PictureAsPdfIcon sx={{ color: "#e53935", fontSize: 22 }} />
            <Box flex={1} sx={{ overflow: "hidden" }}>
              <Typography
                variant="caption"
                fontWeight={700}
                display="block"
                noWrap
              >
                {pdfFileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Will be uploaded &amp; attached to the message
              </Typography>
            </Box>
            <Chip
              label="PDF"
              size="small"
              sx={{
                bgcolor: "#ffebee",
                color: "#c62828",
                fontSize: "0.65rem",
                height: 20,
              }}
            />
          </Box>
        )}

        {/* ── Message editor ── */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.8,
              bgcolor: "#ece5dd",
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="caption" fontWeight={700} color="#555">
              Message Preview &amp; Edit
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {message.length} chars
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                p: 1.5,
                fontSize: "0.82rem",
                lineHeight: 1.7,
                fontFamily: "monospace",
              },
            }}
            placeholder="Type your message here…"
            disabled={sending}
          />
        </Box>

        {/* ── Sending progress ── */}
        {sending && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mt: 2,
              p: 1.5,
              bgcolor: "#e8f5e9",
              borderRadius: 2,
            }}
          >
            <CircularProgress size={18} sx={{ color: "#25d366" }} />
            <Typography variant="body2" color="#1b5e20" fontWeight={500}>
              {stepLabel || "Processing…"}
            </Typography>
          </Box>
        )}

        {/* ── Sent confirmation ── */}
        {sent && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mt: 2,
              p: 1.5,
              bgcolor: "#e8f5e9",
              borderRadius: 2,
            }}
          >
            <CheckCircleIcon sx={{ color: "#25d366", fontSize: 22 }} />
            <Typography variant="body2" color="#1b5e20" fontWeight={600}>
              Message sent successfully!
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5, bgcolor: "#fff", gap: 1 }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          disabled={sending}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          size="small"
          disabled={sending || sent || !normalised || !message.trim()}
          startIcon={
            sending ? (
              <CircularProgress size={14} color="inherit" />
            ) : sent ? (
              <CheckCircleIcon />
            ) : (
              <SendIcon />
            )
          }
          sx={{
            borderRadius: 2,
            bgcolor: "#25d366",
            "&:hover": { bgcolor: "#1ebe5d" },
            "&.Mui-disabled": { bgcolor: "#a5d6a7", color: "#fff" },
            minWidth: 130,
          }}
        >
          {sending
            ? "Sending…"
            : sent
            ? "Sent!"
            : hasPdf
            ? "Upload & Send"
            : "Send Message"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
